// Settings › Agents and tokens (Console D, views/settings.js `#/settings/agents`, APS-310): how an
// agent connects (the MCP server, the CLI, the skills), the agents that may write for you (grants,
// D98), the project's access tokens (D101), the agent handoff (D44, D96: GitHub or Linear, on or
// off, the rule that hands issues off) and what was handed off. What agents did is on Agents.
import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { isApiError } from '../api/client'
import {
  agentGrantsQuery,
  agentSummaryQuery,
  alertDestinationsQuery,
  alertRulesQuery,
  handoffsQuery,
  membersQuery,
  orgQuery,
} from '../api/queries'
import type { AlertDestination } from '../api/types'
import {
  Btn,
  btnClass,
  CodeTabs,
  copyText,
  EmptyNote,
  PageHead,
  Section,
  useToast,
} from '../components/d'
import { Icon } from '../components/icons'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { PRESET_LABEL, ruleSummary } from '../lib/alerts'
import { ConnectedAgents } from './AgentAccessSettings'
import { MCP_COMMAND } from './Agents'
import {
  HandoffChoose,
  HandoffConnect,
  HandoffSwitch,
  HandoffsTable,
  handoffWhere,
  isHandoff,
} from './HandoffSettings'
import { AccessTokens } from './ProjectTokens'
import type { SettingsSearch } from './search'

const WEEK = 7

export function AgentsSettings({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const { t } = useI18n()
  const search = useSearch({ strict: false }) as SettingsSearch
  const { toast, show } = useToast()
  const grants = useQuery(agentGrantsQuery(orgId))
  const summary = useQuery(agentSummaryQuery(orgId, projectId, WEEK))
  return (
    <>
      <PageHead
        crumb={crumb}
        title={title}
        meta={grants.data ? t('settings.agents.meta', { count: grants.data.length }) : undefined}
        actions={
          <>
            <Link
              className={btnClass()}
              to="/o/$orgId/p/$projectId/agents"
              params={{ orgId, projectId }}
            >
              {t('settings.agents.whatTheyDid')}
            </Link>
            <Btn
              onClick={async () =>
                show((await copyText(MCP_COMMAND)) ? t('agents.mcpCopied') : t('issues.copyFailed'))
              }
            >
              <Icon name="copy" className="se-i" />
              {t('agents.copyMcp')}
            </Btn>
          </>
        }
      />
      <ConnectedAgents
        orgId={orgId}
        projectId={projectId}
        summary={summary.data}
        client={search.client}
        capability={search.capability}
      />
      <AccessTokens orgId={orgId} projectId={projectId} />
      <div className="grid2">
        <Section title={t('agents.connect')}>
          <ConnectCode />
        </Section>
        <AgentHandoff orgId={orgId} projectId={projectId} />
      </div>
      <Handoffs orgId={orgId} projectId={projectId} />
      {toast}
    </>
  )
}

/** How to connect: the MCP server, the CLI and the skills, each with its note and Copy. */
function ConnectCode() {
  const { t, rich } = useI18n()
  const tabs = [
    { id: 'mcp', label: 'MCP', note: rich('agents.mcpBody'), lines: [MCP_COMMAND] },
    {
      id: 'cli',
      label: 'CLI',
      note: rich('agents.cliBody'),
      lines: ['apsio issues --json', 'apsio releases compare 4.12.0 4.13.0 --json'],
    },
    {
      id: 'skills',
      label: 'Skills',
      note: rich('agents.skillsBody'),
      lines: ['npx skills add apsio/apsio-skills'],
    },
  ]
  return (
    <CodeTabs
      label={t('agents.connect')}
      notesAbove
      tabs={tabs.map(({ lines, ...tab }) => ({
        ...tab,
        code: lines.join('\n'),
        copy: lines.join('\n'),
      }))}
    />
  )
}

/**
 * The agent handoff: each GitHub or Linear destination, On or Off, where it goes, the rules that
 * hand issues off to it and how many a day; owners connect another. Back from GitHub or Linear,
 * the owner chooses the repository or the team here.
 */
function AgentHandoff({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useI18n()
  const search = useSearch({ strict: false }) as SettingsSearch
  const org = useQuery(orgQuery(orgId))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  const role = org.data?.role
  const owner = role === 'owner'
  const handoff = (destinations.data?.destinations ?? []).filter(isHandoff)
  const one = handoff.length === 1 ? handoff[0] : undefined
  return (
    <Section
      title={t('settings.handoff.title')}
      meta={one ? <span translate="no">{handoffWhere(one)}</span> : undefined}
      right={one ? <HandoffSwitch orgId={orgId} destination={one} role={role} /> : undefined}
    >
      {search.handoff_error ? (
        <p role="alert" className="notice error">
          {t('settings.handoff.connectFailed', { code: search.handoff_error })}
        </p>
      ) : null}
      {owner && search.handoff ? (
        <HandoffChoose orgId={orgId} connectionId={search.handoff} />
      ) : null}
      {destinations.isPending || org.isPending ? (
        <Loading />
      ) : destinations.isError ? (
        isApiError(destinations.error, 503) ? (
          <p className="se-hint">{t('settings.handoff.notSetUp')}</p>
        ) : (
          <ErrorState error={destinations.error} retry={() => destinations.refetch()} />
        )
      ) : (
        <>
          {handoff.length === 0 ? <p className="se-hint">{t('settings.handoff.about')}</p> : null}
          {handoff.map((d) => (
            <HandoffDestination
              key={d.id}
              orgId={orgId}
              projectId={projectId}
              destination={d}
              role={role}
              alone={handoff.length === 1}
            />
          ))}
          {handoff.length > 0 ? (
            <p className="se-hint" style={{ paddingTop: 10 }}>
              {t('settings.handoff.never')}
            </p>
          ) : null}
          {owner ? (
            <HandoffConnect orgId={orgId} projectId={projectId} />
          ) : handoff.length === 0 ? (
            <p className="se-hint">{t('settings.handoff.ownersOnly')}</p>
          ) : null}
        </>
      )}
    </Section>
  )
}

function HandoffDestination({
  orgId,
  projectId,
  destination: d,
  role,
  alone,
}: {
  orgId: string
  projectId: string
  destination: AlertDestination
  role: 'owner' | 'admin' | 'member' | undefined
  alone: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  const rules = useQuery(alertRulesQuery(orgId, projectId))
  const members = useQuery(membersQuery(orgId))
  const sending = (rules.data?.rules ?? []).filter((r) => r.destination_id === d.id && r.enabled)
  const by = members.data?.find((m) => m.user_id === d.created_by)
  return (
    <>
      {alone ? null : (
        <div className="se-cfg">
          <span>
            <b translate="no">{d.name}</b>
            <span translate="no">{handoffWhere(d)}</span>
          </span>
          <HandoffSwitch orgId={orgId} destination={d} role={role} />
        </div>
      )}
      <div className="kv se-kv">
        <span>
          {t(d.kind === 'github' ? 'settings.handoff.repository' : 'settings.handoff.team')}
        </span>
        <span translate="no">{handoffWhere(d)}</span>
        <span>{t('settings.handoff.connected')}</span>
        <span>
          {t(d.kind === 'github' ? 'settings.handoff.viaGitHub' : 'settings.handoff.viaLinear')}
          {by ? ` · ${t('settings.handoff.by', { name: by.name || by.email })}` : ''}
        </span>
        <span>{t('settings.handoff.rule')}</span>
        <span>
          {rules.isPending ? (
            '…'
          ) : sending.length === 0 ? (
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/alerts"
              params={{ orgId, projectId }}
              search={{ tab: 'rules' }}
            >
              {t('settings.handoff.noRule')}
            </Link>
          ) : (
            sending.map((r) => `${t(PRESET_LABEL[r.preset])}: ${ruleSummary(r, i18n)}`).join(' · ')
          )}
        </span>
        <span>{t('settings.handoff.then')}</span>
        <span>
          {t(d.kind === 'github' ? 'settings.handoff.thenGitHub' : 'settings.handoff.thenLinear')}
        </span>
        <span>{t('settings.handoff.perDay')}</span>
        <span>{t('settings.handoff.atMost', { n: d.daily_cap ?? 5 })}</span>
      </div>
    </>
  )
}

/** What this project handed off, with each issue's own state. */
function Handoffs({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useI18n()
  const list = useQuery(handoffsQuery(orgId, projectId))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  if (list.isPending || (list.isError && isApiError(list.error, 503))) return null
  const rows = list.data?.handoffs ?? []
  return (
    <Section title={t('agents.handoffs')} meta={t('agents.handoffsMeta')}>
      {list.isError ? (
        <ErrorState error={list.error} retry={() => list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyNote title={t('agents.handoffsNone')}>{t('agents.handoffsNoneBody')}</EmptyNote>
      ) : (
        <HandoffsTable
          orgId={orgId}
          projectId={projectId}
          handoffs={rows}
          destinations={destinations.data?.destinations ?? []}
          withState
        />
      )}
    </Section>
  )
}
